/**
 * El backend devuelve SIEMPRE la misma forma de error: {"error": {code, mensaje}}
 * (trazabis-agendamiento; el FastAPI antiguo decia `codigo`, que se sigue leyendo).
 * Aun asi, un proxy o un balanceador pueden colarse con HTML, asi que el parser
 * nunca asume que el cuerpo sea el esperado.
 */
export class ErrorApi extends Error {
  readonly codigo: string
  readonly mensaje: string
  readonly estado: number

  constructor(estado: number, codigo: string, mensaje: string) {
    super(mensaje)
    this.name = 'ErrorApi'
    this.estado = estado
    this.codigo = codigo
    this.mensaje = mensaje
  }

  /**
   * La funcion no existe en el backend (501 `no_disponible`, ver `enlace.ts`).
   * Reintentar no la va a crear.
   */
  get esNoDisponible(): boolean {
    return this.estado === 501
  }

  /**
   * Falta configurar una integracion en el servidor (variables de entorno):
   * reintentar no lo arregla. `integracion_sin_configurar` (FastAPI antiguo) o
   * `servicio_no_disponible` (trazabis-agendamiento, p. ej. n8n sin URL).
   */
  get esSinConfigurar(): boolean {
    return (
      this.codigo === 'integracion_sin_configurar' ||
      (this.estado === 503 && this.codigo === 'servicio_no_disponible')
    )
  }

  /** El unico caso que obliga a devolver al usuario a la pantalla de acceso. */
  get esSesionCaducada(): boolean {
    return this.estado === 401
  }
}

const MENSAJE_GENERICO = 'No se ha podido completar la operación. Inténtalo de nuevo.'

export async function interpretarError(respuesta: Response): Promise<ErrorApi> {
  let codigo = 'desconocido'
  let mensaje = MENSAJE_GENERICO

  try {
    const cuerpo = await respuesta.json()
    const codigoCuerpo = cuerpo?.error?.code ?? cuerpo?.error?.codigo
    if (codigoCuerpo) {
      codigo = String(codigoCuerpo)
      mensaje = String(cuerpo.error.mensaje ?? MENSAJE_GENERICO)
    }
  } catch {
    // Cuerpo no-JSON: un proxy intermedio o un error de red. Nos quedamos
    // con el mensaje generico, que es lo unico honesto que podemos decir.
  }

  return new ErrorApi(respuesta.status, codigo, mensaje)
}
